import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { ArrowRight, ArrowUp, Bot, LoaderCircle, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ChatExamples, SearchingExamples } from '@/components/chat/ChatExamples';
import { apiClient, type CardChatExampleSet, type CardChatHistoryTurn } from '@/services';

const maxMessageLength = 2_000;

type PresetQuestionId = 'compare' | 'origin' | 'sentence';

const presetQuestions: { id: PresetQuestionId; label: string }[] = [
  { id: 'compare', label: 'Compare with…' },
  { id: 'origin', label: 'Explain the origin of the word' },
  { id: 'sentence', label: 'Use the word in a sentence' },
];

const buildCompareQuestion = (other: string) => `How is it different from “${other.trim()}”?`;

const focusRingClassName =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#496653] focus-visible:ring-offset-1';

type EmphasisPart = {
  text: string;
  style: 'plain' | 'bold' | 'italic';
};

const parseEmphasis = (text: string): EmphasisPart[] => {
  const parts: EmphasisPart[] = [];
  let position = 0;

  const append = (value: string, style: EmphasisPart['style']) => {
    if (!value) return;
    const previous = parts.at(-1);
    if (previous?.style === style) {
      previous.text += value;
    } else {
      parts.push({ text: value, style });
    }
  };

  while (position < text.length) {
    if (text.startsWith('**', position)) {
      const end = text.indexOf('**', position + 2);
      if (end > position + 2) {
        append(text.slice(position + 2, end), 'bold');
        position = end + 2;
        continue;
      }

      append('**', 'plain');
      position += 2;
      continue;
    }

    if (text[position] === '*') {
      const end = text.indexOf('*', position + 1);
      if (end > position + 1) {
        append(text.slice(position + 1, end), 'italic');
        position = end + 1;
        continue;
      }

      append('*', 'plain');
      position += 1;
      continue;
    }

    const nextMarker = text.indexOf('*', position);
    const end = nextMarker === -1 ? text.length : nextMarker;
    append(text.slice(position, end), 'plain');
    position = end;
  }

  return parts;
};

function FormattedAssistantText({ text }: { text: string }) {
  return parseEmphasis(text).map((part, index) => {
    if (part.style === 'bold') {
      return <strong key={index}>{part.text}</strong>;
    }
    if (part.style === 'italic') {
      return <em key={index}>{part.text}</em>;
    }
    return <span key={index}>{part.text}</span>;
  });
}

interface CardChatPanelProps {
  meaningId: string;
  term: string;
  alternatives: string[];
  open: boolean;
  panelId?: string;
  pendingQuestion?: { id: number; text: string } | null;
}

export function CardChatPanel({
  meaningId,
  term,
  alternatives,
  open,
  panelId = 'card-chat-panel',
  pendingQuestion = null,
}: CardChatPanelProps) {
  const [turns, setTurns] = useState<CardChatHistoryTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [pendingMessage, setPendingMessage] = useState('');
  const [streamingAnswer, setStreamingAnswer] = useState('');
  const [streamingExamples, setStreamingExamples] = useState<CardChatExampleSet[]>([]);
  const [searching, setSearching] = useState(false);
  const [retryMessage, setRetryMessage] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const activeMeaningIdRef = useRef(meaningId);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    activeMeaningIdRef.current = meaningId;
    abortControllerRef.current?.abort();
    setTurns([]);
    setDraft('');
    setPendingMessage('');
    setStreamingAnswer('');
    setStreamingExamples([]);
    setSearching(false);
    setRetryMessage('');
    setError('');
    setSending(false);
    setComparing(false);
    setLoadingHistory(true);

    let cancelled = false;
    apiClient
      .getCardChat(meaningId)
      .then((history) => {
        if (!cancelled) setTurns(history);
      })
      .catch(() => {
        if (!cancelled) setError('Couldn’t load the previous conversation.');
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });

    return () => {
      cancelled = true;
    };
  }, [meaningId]);

  // Abort an in-flight stream if the panel unmounts entirely (not just a meaningId change,
  // which is already handled above).
  useEffect(() => () => abortControllerRef.current?.abort(), []);

  useEffect(() => {
    if (!open) return;

    const frame = requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, pendingMessage, sending, streamingAnswer, streamingExamples, searching, turns, comparing]);

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const sendMessage = async (message: string) => {
    const normalized = message.trim();
    if (!normalized || sending || loadingHistory) return;
    const requestMeaningId = meaningId;

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setSending(true);
    setError('');
    setRetryMessage('');
    setDraft('');
    setPendingMessage(normalized);
    setStreamingAnswer('');
    setStreamingExamples([]);
    setSearching(false);

    let answer = '';
    const foundExamples: CardChatExampleSet[] = [];
    try {
      await apiClient.chatWithMeaningCard(
        requestMeaningId,
        normalized,
        (delta) => {
          if (activeMeaningIdRef.current !== requestMeaningId) return;
          setSearching(false);
          answer += delta;
          setStreamingAnswer(answer);
        },
        controller.signal,
        (event) => {
          if (activeMeaningIdRef.current !== requestMeaningId) return;
          if (event.type === 'searching') {
            setSearching(true);
            return;
          }
          setSearching(false);
          foundExamples.push(event.examples);
          setStreamingExamples([...foundExamples]);
        }
      );
      if (activeMeaningIdRef.current !== requestMeaningId) return;
      setTurns((current) => [
        ...current,
        { userMessage: normalized, assistantMessage: answer, examples: foundExamples },
      ]);
      setPendingMessage('');
      setStreamingAnswer('');
    } catch (exception) {
      if (exception instanceof DOMException && exception.name === 'AbortError') return;
      if (activeMeaningIdRef.current !== requestMeaningId) return;
      setPendingMessage('');
      setStreamingAnswer('');
      setStreamingExamples([]);
      setSearching(false);
      setDraft(normalized);
      setRetryMessage(normalized);
      setError('The AI assistant is unavailable right now. Please try again.');
    } finally {
      if (activeMeaningIdRef.current === requestMeaningId) {
        setSending(false);
      }
    }
  };

  const sentQuestionIdRef = useRef(0);
  useEffect(() => {
    if (!pendingQuestion || loadingHistory || sending) return;
    if (pendingQuestion.id === sentQuestionIdRef.current) return;
    sentQuestionIdRef.current = pendingQuestion.id;
    void sendMessage(pendingQuestion.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingQuestion, loadingHistory, sending]);

  const startNewChat = async () => {
    if (sending || loadingHistory) return;
    const requestMeaningId = meaningId;
    setError('');
    try {
      await apiClient.resetCardChat(requestMeaningId);
      if (activeMeaningIdRef.current !== requestMeaningId) return;
      setTurns([]);
      setRetryMessage('');
      setComparing(false);
    } catch {
      if (activeMeaningIdRef.current !== requestMeaningId) return;
      setError('Couldn’t start a new chat. Please try again.');
    }
  };

  const focusInput = () => requestAnimationFrame(() => inputRef.current?.focus());

  const startCompare = () => {
    setDraft('');
    setComparing(true);
    focusInput();
  };

  const clearCompare = () => {
    setComparing(false);
    focusInput();
  };

  const compareWith = (other: string) => {
    if (!other.trim() || sending) return;
    setComparing(false);
    void sendMessage(buildCompareQuestion(other));
  };

  const selectPreset = (id: PresetQuestionId) => {
    if (sending) return;
    if (id === 'compare') {
      startCompare();
      return;
    }
    if (id === 'origin') {
      void sendMessage(`Explain the origin of the word “${term}”.`);
      return;
    }
    void sendMessage(`Use “${term}” in a new sentence.`);
  };

  const submitDraft = () => {
    if (comparing) {
      compareWith(draft);
      return;
    }
    void sendMessage(draft);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitDraft();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitDraft();
      return;
    }
    if (!comparing) return;
    if (event.key === 'Escape' || (event.key === 'Backspace' && draft === '')) {
      event.preventDefault();
      event.stopPropagation();
      setComparing(false);
    }
  };

  const chatEmpty = turns.length === 0 && !pendingMessage;
  const showSuggestions = !chatEmpty && !sending && !comparing && draft === '';
  const canStartNewChat = turns.length > 0 && !sending && !loadingHistory;

  if (!open) return null;

  return (
    <div id={panelId} className="flex h-full min-h-0 flex-col">
      <div
        role="log"
        aria-live="polite"
        aria-relevant="additions text"
        className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5"
      >
        {chatEmpty && (
          <div className="mx-auto flex max-w-sm flex-col items-center py-10 text-center">
            <div className="flex size-11 items-center justify-center rounded-full bg-[#e7f0e8] text-[#2f4938]">
              <Bot className="size-5" aria-hidden="true" />
            </div>
            <p className="mt-4 font-semibold text-slate-900">Ask about “{term}”</p>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              I know this card’s context and can explain nuances, alternatives, and natural usage.
            </p>
            {!comparing && (
              <ul className="mt-6 w-full divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white text-left">
                {presetQuestions.map((question) => (
                  <li key={question.id}>
                    <button
                      type="button"
                      disabled={sending}
                      onClick={() => selectPreset(question.id)}
                      className={`group flex w-full items-center justify-between gap-3 px-4 py-3.5 text-sm text-slate-800 transition-colors hover:bg-slate-50 focus-visible:ring-inset disabled:opacity-60 ${focusRingClassName}`}
                    >
                      <span>{question.label}</span>
                      <ArrowRight
                        className="size-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5"
                        aria-hidden="true"
                      />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {turns.map((turn, index) => (
          <div key={`${index}-${turn.userMessage}`} className="space-y-3">
            <div className="ml-auto max-w-[86%] rounded-2xl rounded-br-md bg-slate-100 px-4 py-3 text-sm leading-6 whitespace-pre-wrap text-slate-800">
              {turn.userMessage}
            </div>
            {turn.examples.map((examples, exampleIndex) => (
              <ChatExamples key={exampleIndex} meaningId={meaningId} examples={examples} />
            ))}
            {turn.assistantMessage && (
              <div className="mr-auto max-w-[92%] text-sm leading-6 whitespace-pre-wrap text-slate-800">
                <FormattedAssistantText text={turn.assistantMessage} />
              </div>
            )}
          </div>
        ))}

        {pendingMessage && (
          <div className="space-y-3">
            <div className="ml-auto max-w-[86%] rounded-2xl rounded-br-md bg-slate-100 px-4 py-3 text-sm leading-6 whitespace-pre-wrap text-slate-800">
              {pendingMessage}
            </div>
            {streamingExamples.map((examples, exampleIndex) => (
              <ChatExamples key={exampleIndex} meaningId={meaningId} examples={examples} />
            ))}
            {searching && <SearchingExamples />}
            {streamingAnswer ? (
              <div className="mr-auto max-w-[92%] text-sm leading-6 whitespace-pre-wrap text-slate-800">
                <FormattedAssistantText text={streamingAnswer} />
              </div>
            ) : !searching && streamingExamples.length === 0 ? (
              <div className="mr-auto flex w-fit items-center gap-2 py-1 text-sm text-slate-500">
                <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
                Thinking…
              </div>
            ) : null}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <div className="border-t border-slate-100 bg-white px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
        {error && (
          <div role="alert" className="mb-3 flex items-center justify-between gap-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
            <span>{error}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={sending}
              onClick={() => void sendMessage(retryMessage)}
              className="h-8 shrink-0 rounded-full text-red-700 hover:bg-red-100"
            >
              Try again
            </Button>
          </div>
        )}
        {showSuggestions && (
          <div className="mb-3 flex flex-wrap gap-2">
            {presetQuestions.map((question) => (
              <button
                key={question.id}
                type="button"
                onClick={() => selectPreset(question.id)}
                className={`h-9 shrink-0 rounded-full border border-dashed border-[#a9c2b0] px-3.5 text-sm font-semibold text-[#2f5c4e] transition-colors hover:bg-[#e7f0e8] ${focusRingClassName}`}
              >
                {question.label}
              </button>
            ))}
            {canStartNewChat && (
              <button
                type="button"
                onClick={() => void startNewChat()}
                className={`h-9 shrink-0 rounded-full px-3.5 text-sm font-semibold text-slate-500 transition-colors hover:bg-slate-100 ${focusRingClassName}`}
              >
                New chat
              </button>
            )}
          </div>
        )}
        {comparing && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {alternatives.length > 0 && (
              <>
                <span className="shrink-0 text-[10.5px] font-medium tracking-wider text-[#97917f] uppercase">Alternatives</span>
                {alternatives.map((alternative) => (
                  <button
                    key={alternative}
                    type="button"
                    disabled={sending}
                    onClick={() => compareWith(alternative)}
                    className={`h-[34px] shrink-0 rounded-full border border-[#d9cbb7] bg-[#fffaf1] px-3 text-sm font-semibold text-[#17231f] transition-colors hover:border-[#496653] disabled:opacity-60 ${focusRingClassName}`}
                  >
                    {alternative}
                  </button>
                ))}
              </>
            )}
          </div>
        )}
        <form onSubmit={handleSubmit} className="flex items-end gap-2">
          <label htmlFor={`${panelId}-input`} className="sr-only">
            {comparing ? 'Word or phrase to compare with' : 'Ask about this card'}
          </label>
          <div className="flex min-h-11 flex-1 items-center gap-1.5 rounded-2xl border border-slate-200 bg-slate-50 transition-colors focus-within:border-[#496653] focus-within:bg-white">
            {comparing && (
              <span className="ml-1.5 flex h-8 shrink-0 items-center gap-1 rounded-full bg-[#e7f0e8] pr-1 pl-2.5 text-sm font-semibold text-[#2f5c4e]">
                Different from
                <button
                  type="button"
                  onClick={clearCompare}
                  aria-label="Remove template"
                  className={`flex size-[22px] items-center justify-center rounded-full transition-colors hover:bg-[#d5e5d8] ${focusRingClassName}`}
                >
                  <X className="size-3.5" aria-hidden="true" />
                </button>
              </span>
            )}
            <textarea
              ref={inputRef}
              id={`${panelId}-input`}
              value={draft}
              maxLength={maxMessageLength}
              rows={1}
              disabled={sending}
              placeholder={comparing ? 'any word or phrase…' : 'Ask about this word…'}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleKeyDown}
              className={`field-sizing-content max-h-28 min-h-[42px] min-w-0 flex-1 resize-none bg-transparent py-2.5 pr-4 text-sm leading-6 text-slate-900 outline-none placeholder:text-slate-400 disabled:opacity-60 ${comparing ? 'pl-1' : 'pl-4'}`}
            />
          </div>
          <Button
            type="submit"
            size="icon"
            disabled={sending || !draft.trim()}
            aria-label="Send message"
            className={`size-11 rounded-full bg-[#2f5c4e] text-white hover:bg-[#264c40] ${focusRingClassName}`}
          >
            {sending ? <LoaderCircle className="size-4 animate-spin" /> : <ArrowUp className="size-5" strokeWidth={2.25} />}
          </Button>
        </form>
      </div>
    </div>
  );
}
